import { FC, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  Link,
} from '@mui/material'
import { Autocomplete } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useForm, Controller } from 'react-hook-form'

// Components
import { AppDialog, Loader } from '../../../components'

// Redux
import {
  getFormattedEndDateString,
  getFormattedStartDateString,
  getFirstDayoftheCurrentMonth,
  getFormattedDateTimeIST,
  getEncodedFormattedDate,
  getEndDateOfMonth,
  getBillFormat,
  setBillFormat,
} from '../../../utils'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  createMonthlyBillAction,
  createMonthlyBillResetAction,
  fetchMonthlyBillsAction,
} from '../../../store/monthlyBills'
import { getInvoiceNumberAction } from '../../../store/slips'
import { showAlertAction } from '../../../store/alerts'
import { getNextInvoiceNo } from './Services'
import { ChallanDropDown } from '../slips/ChallanDropDown'
import {
  Bill,
  Challan,
  fetchBillFormats,
  intersectFormatLists,
  MonthlyBill,
} from '../../../../src/services'
import { SiteDto } from '../../../../src/components/SitesAutoComplete/Services'
import SiteAutocomplete from '../../../../src/components/SitesAutoComplete/SiteAutocomplete'
import { useCommandPaletteActions } from '../../../contexts/CommandPaletteContext'

type FormInputs = {
  discount: number
  createDate: Date
  startDate: Date
  endDate: Date
  month: Date
  invoiceNo: number
  billFormat?: string // Add billFormat to the form inputs
}

export interface CreateMonthlyBillProps {
  onClose: (site?: SiteDto, result?: boolean) => void
  account?: Bill,
  selectedSiteId?: number,
  monthlyBill?: MonthlyBill
}

export const CreateMonthlyBill: FC<CreateMonthlyBillProps> = ({
  onClose,
  account,
  selectedSiteId,
  monthlyBill
}) => {
  const dispatchAction = useAppDispatch()
  const { openNewSite } = useCommandPaletteActions()
  const { loading, success, message } = useAppSelector(
    (state) => state.createMonthlyBill
  )
  const { data: getInvoiceData } = useAppSelector((state) => state.getInvoice)

  // Add state to track highlighted fields
  const [highlightedFields, setHighlightedFields] = useState<Set<string>>(new Set())

  // Function to highlight a field when it's auto-updated
  const highlightField = (fieldName: string) => {
    setHighlightedFields(prev => new Set(prev).add(fieldName))
    // Remove highlight after 3 seconds
    setTimeout(() => {
      setHighlightedFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(fieldName)
        return newSet
      })
    }, 3000)
  }

  // Helper function to get highlighting styles for a field
  const getHighlightStyles = (fieldName: string) => {
    if (highlightedFields.has(fieldName)) {
      return {
        backgroundColor: '#fff3cd',
        border: '2px solid #ffc107',
        borderRadius: '4px',
        animation: 'pulse 1s ease-in-out',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
          '100%': { transform: 'scale(1)' }
        }
      }
    }
    return {}
  }

  const defaultValues = {
    discount: 0,
    createDate: new Date(),
    startDate: getFirstDayoftheCurrentMonth(),
    endDate: new Date(),
    month: getFirstDayoftheCurrentMonth(),
    invoiceNo: getInvoiceData?.docNo ?? '',
  }

  const { control, handleSubmit, reset, getValues, setValue, watch } = useForm<FormInputs>({
    defaultValues,
    mode: 'all',
  })
  const [billId, setBillId] = useState<number>(0)
  const [ratePerSqft, setRatePerSqft] = useState<number | null>(null)
  const [site, setSite] = useState<SiteDto | null>(null)
  const [supportedFormats, setSupportedFormats] = useState<string[]>([])
  const [formatsLoading, setFormatsLoading] = useState(false)
  const [formatsError, setFormatsError] = useState<string | null>(null)

  // Sample bill format PDFs mapping
  const billFormatSamples: Record<string, string> = {
    'FORMAT_1': 'https://scaffoldingapp.com/pb-doc-service/2026/permanent/INVOICE-fb3ee87f-03ec-4ec0-b999-ee7904359eeb.pdf',
    'FORMAT_2': 'https://scaffoldingapp.com/pb-doc-service/2026/permanent/INVOICE-5f05cc46-bd93-4f82-8e6a-e1e5986af13d.pdf',
    'FORMAT_3': 'https://scaffoldingapp.com/pb-doc-service/2026/temporary/INVOICE_-24881e83-65b6-4b48-ad69-967c7d758ec2.pdf',
    'FORMAT_4': 'https://scaffoldingapp.com/pb-doc-service/2026/permanent/INVOICE-2bd1f7b8-c460-4fb1-be02-5d90dd8e1b72.pdf',
    'FORMAT_5': 'https://scaffoldingapp.com/pb-doc-service/2026/temporary/INVOICE_-99152f9c-8f49-4bb7-a98a-65597d95ccb1.pdf',
  }

  const [isClose] = useState<boolean>(false)
  const [showChallan, setShowChallan] = useState<boolean>(false)
  const [dateFilter, setDateFilter] = useState('month')
  const [selectedChallan, setSelectedChallan] = useState<Challan | null>()
  const { data: userData } = useAppSelector((state) => state.userData)
  const [fullInvNo, setFullInvNo] = useState<string | null>(null)

  const fetchInvoiceNoByDate = ((date: string) => {
    getNextInvoiceNo(billId, date).then(
      (response) => {
        if (response.success) {
          // Only update invoice number automatically if:
          // - creating a new bill (monthlyBill is undefined), OR
          // - editing and the current invoice field is empty
          const currentInvoice: any = getValues('invoiceNo' as any);
          const isEmpty = currentInvoice === '' || currentInvoice === null || currentInvoice === undefined;
          const shouldUpdate = !monthlyBill || isEmpty;
          if (shouldUpdate) {
            setValue("invoiceNo", response.data.docNo)
            // Highlight the invoice number field to show it was auto-updated
            highlightField('invoiceNo')
          }
          if (response.data?.fullInvNo != null) {
            setFullInvNo(response.data.fullInvNo)
          }
        } else {
          dispatchAction(
            showAlertAction({
              message: response.message,
              severity: 'error',
            })
          )
        }
      }
    )
  })

  function showBillFormat() {
    return billId != null && billId != undefined && billId > 0 && (ratePerSqft === 0 || !ratePerSqft)
  }

  useEffect(() => {
    const canShow =
      billId > 0 && (ratePerSqft === 0 || !ratePerSqft)
    if (!canShow) {
      setSupportedFormats([])
      setFormatsError(null)
      setFormatsLoading(false)
      return
    }
    let cancelled = false
    setFormatsLoading(true)
    setFormatsError(null)
    fetchBillFormats(billId)
      .then((res) => {
        if (cancelled) return
        if (res.success && Array.isArray(res.data)) {
          const sorted = intersectFormatLists([res.data])
          setSupportedFormats(sorted)
          if (sorted.length === 0) {
            setFormatsError('No bill formats are allowed for this site.')
          }
        } else {
          setSupportedFormats([])
          setFormatsError(res.message || 'Could not load bill formats.')
        }
      })
      .catch(() => {
        if (cancelled) return
        setSupportedFormats([])
        setFormatsError('Could not load bill formats.')
      })
      .finally(() => {
        if (!cancelled) setFormatsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [billId, ratePerSqft])

  useEffect(() => {
    if (!showBillFormat() || formatsLoading) return
    if (supportedFormats.length === 0) {
      setValue('billFormat', undefined)
      return
    }
    const current = getValues('billFormat')
    const session = getBillFormat()
    const next =
      current && supportedFormats.includes(current)
        ? current
        : session && supportedFormats.includes(session)
          ? session
          : supportedFormats[0]
    setValue('billFormat', next)
  }, [supportedFormats, formatsLoading, billId, ratePerSqft, setValue, getValues])

  const handleDateFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDateFilter((event.target as HTMLInputElement).value)
  }

  // At the top of the component, add this useEffect to populate form when editing:
useEffect(() => {
  if (monthlyBill) {
    // Parse dates from string to Date object if necessary
    const parsedCreateDate = new Date(monthlyBill.createDate);
    const parsedStartDate = monthlyBill.startDate ? new Date(monthlyBill.startDate) : null;
    const parsedEndDate = monthlyBill.endDate ? new Date(monthlyBill.endDate) : null;

    // Determine whether to show date-range mode or month mode
    const mode = parsedStartDate && parsedEndDate ? 'date-range' : 'month';
    setDateFilter(mode);

    reset({
      createDate: parsedCreateDate,
      startDate: parsedStartDate ?? getFirstDayoftheCurrentMonth(),
      endDate: parsedEndDate ?? getEndDateOfMonth(new Date()),
      invoiceNo: Number(monthlyBill.invoiceNo),
      discount: monthlyBill.discount ?? 0,
      month: parsedStartDate ?? getFirstDayoftheCurrentMonth(),
      billFormat: getBillFormat() ?? undefined,
    });
  }
}, [monthlyBill, reset]);

  useEffect(() => {
    if (account) {
      setBillId(account.id)
      setRatePerSqft(account.ratePerSqft)
    }
  }, [account])

  useEffect(() => {
    setFullInvNo(null)
    dispatchAction(createMonthlyBillResetAction())
    if (billId) {
      dispatchAction(getInvoiceNumberAction(billId))
    }
  }, [billId])

  useEffect(() => {
    if (userData?.config) {
      setShowChallan(userData.config.enabledChallanBilling);
    } else {
      setShowChallan(false); // Default if config is not available
    }
  }, [userData]);

  useEffect(() => {
    const invoiceNo = monthlyBill?.invoiceNo ?? getInvoiceData?.docNo ?? ''
    reset({ ...getValues(), invoiceNo })
  }, [getInvoiceData, monthlyBill])
  

  const submitHandle = handleSubmit((data: FormInputs) => {
    const { startDate, endDate, invoiceNo, createDate, discount, billFormat } = data
    if (!createDate || (dateFilter !== "month" && (!startDate || !endDate))) {
      return false
    }
    let date1 = startDate
    let date2 = endDate
    if (dateFilter === "month") {
      date1 = null
      date2 = null
    }
    if (showBillFormat()) {
      if (formatsLoading) {
        dispatchAction(
          showAlertAction({ message: 'Loading bill formats…', severity: 'error' })
        )
        return false
      }
      if (supportedFormats.length === 0) {
        dispatchAction(
          showAlertAction({
            message:
              formatsError ||
              'No bill format is available for this site. Check site settings and try again.',
            severity: 'error',
          })
        )
        return false
      }
      if (!billFormat || !supportedFormats.includes(billFormat)) {
        dispatchAction(
          showAlertAction({
            message: 'Select a valid bill format that is allowed for this site.',
            severity: 'error',
          })
        )
        return false
      }
      setBillFormat(billFormat)
    }

    dispatchAction(
      createMonthlyBillAction({
        fromDate: date1 ? getFormattedStartDateString(new Date(startDate), false) : null,
        toDate: date2 ? getFormattedEndDateString(new Date(endDate), false) : null,
        createDate: getFormattedDateTimeIST(createDate),
        accountId: billId,
        invoiceNo: invoiceNo,
        discount: discount,
        challanId: dateFilter === 'date-range' ? selectedChallan?.id : null,
        billFormat: showBillFormat() ? billFormat : null, // Pass the selected bill format to the API
        invoiceId: monthlyBill?.id
      })
    )
  })

  useEffect(() => {
    if (success) {
      dispatchAction(fetchMonthlyBillsAction(billId))
      dispatchAction(
        showAlertAction({
          message: monthlyBill ? 'Successfully updated' : 'Successfully created',
          severity: 'success',
        })
      )
      dispatchAction(createMonthlyBillResetAction())
      onClose(site, true)
    } else if (message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      dispatchAction(createMonthlyBillResetAction())
    }
  }, [success, message])

  return (
    <AppDialog
      id="edit-item"
      title={monthlyBill ? 'Edit Bill' : 'Create Bill'}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      maxWidth="sm"
    >
      {loading && <Loader />}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={0}
      >
         {(!account || account.id === 0) && (
           <Grid item xs={12} ml={1}  mr={1}>
             <SiteAutocomplete 
               selectedSiteId={selectedSiteId} 
               onSiteSelected={(site?: SiteDto) => {
                 setBillId(site?.id)
                 setRatePerSqft(site?.ratePerSqft)
                 setSite(site)
               }}
               onAddNewSite={() => {
                 if (openNewSite) {
                   openNewSite()
                 }
               }}
               onCloseDialog={() => {
                 if (onClose) {
                   onClose()
                 }
               }}
             />
             </Grid>
            )}
        <Grid item xs={12} mr={2}>
          <Box
            component="form"
            sx={{
              '& .MuiTextField-root': { m: 1 },
            }}
            noValidate
            autoComplete="off"
          >
            <Grid item={true} xs={12}>
              <Controller
                control={control}
                name={'createDate'}
                rules={{ required: 'This is required.' }}
                render={({ field, fieldState }) => {
                  const { error } = fieldState
                  const isError = error || field.value == 'Invalid Date'
                  return (
                    <DatePicker
                      label="Invoice date"
                      value={field.value}
                      onChange={(date) => {
                        field.onChange(date)
                        if (date) {
                          fetchInvoiceNoByDate(getEncodedFormattedDate(date))
                        }
                      }}
                      format="dd/MM/yyyy hh:mm a"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          variant: 'outlined',
                          helperText: isError
                            ? error?.message || 'Invalid Date'
                            : null,
                          error: !!isError,
                        },
                      }}
                    />
                  )
                }}
              />
            </Grid>
            {showBillFormat() && (
              <Grid item={true} xs={12}>
                <Controller
                  name="billFormat"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Autocomplete
                        options={supportedFormats.map((f) => ({
                          label: f,
                          id: f,
                        }))}
                        loading={formatsLoading}
                        disabled={
                          formatsLoading || supportedFormats.length === 0
                        }
                        size="small"
                        getOptionLabel={(option) => option.label}
                        value={value ? { label: value, id: value } : null}
                        onChange={(event, newValue) => {
                          onChange(newValue ? newValue.id : null)
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Bill Format"
                            variant="outlined"
                            fullWidth
                            helperText={
                              formatsError ||
                              (formatsLoading
                                ? 'Loading allowed formats…'
                                : null)
                            }
                            error={!!formatsError}
                          />
                        )}
                      />
                      {value === 'FORMAT_1' && (
                        <Typography sx={{ mt: 0.2, ml:1, fontWeight: 'bold', color: '#db6607ff' }}>
                          [Depricated] FORMAT_1 will be removed soon.
                        </Typography>
                      )}
                      {value && billFormatSamples[value] && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0,
                            ml: 1,
                            fontSize: '0.85rem',
                            color: 'text.secondary',
                          }}
                        >
                          To view sample bill click: {' '}
                          <Link
                            href={billFormatSamples[value]}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: '#1976d2',
                              fontWeight: 600,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {value}
                          </Link>
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {/* The rest of your form fields */}
            <Grid container spacing={2} mt={0.5}>
              <Grid item={true} xs={12}>
                <Controller
                  name={'invoiceNo'}
                  control={control}
                  rules={{ required: 'This is required.' }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      size='small'
                      helperText={error ? error.message : null}
                      error={!!error}
                      onChange={onChange}
                      value={value}
                      fullWidth
                      label={'Invoice Number'}
                      variant="outlined"
                      sx={getHighlightStyles('invoiceNo')}
                    />
                  )}
                />
                {!monthlyBill && (
                  <Typography color={'green'} sx={{ fontSize: 12, ml: 1, p: 0 }}>
                    Full Invoice no: {fullInvNo ?? getInvoiceData?.fullInvNo ?? watch('invoiceNo') ?? ''}
                  </Typography>
                )}
              </Grid>

              <Grid item={true} xs={12} sx={{ ml: 1 }}>
                <FormControl>
                  <FormLabel id="date-filter-label">Bill duration</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="date-filter-label"
                    name="date-filter-group"
                    value={dateFilter}
                    onChange={handleDateFilterChange}
                  >
                    <FormControlLabel
                      value="month"
                      control={<Radio />}
                      label="Complete (from start to end)"
                    />
                    <FormControlLabel
                      value="date-range"
                      control={<Radio />}
                      label="Date Range"
                      data-cy="id_radio_date_range"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {dateFilter === 'date-range' && (
                <>
                  {showChallan && (
                    <Grid item={true} xs={12}>
                      <ChallanDropDown
                        accountId={Number(billId)}
                        onChallanChange={(challan) => {
                          setSelectedChallan(challan)
                        }}
                        challan_type={"OUT"}
                      />
                    </Grid>
                  )}
                  <Grid item={true} xs={12} mt={0}>
                    <Controller
                      control={control}
                      name={'startDate'}
                      rules={{ required: 'This is required.' }}
                      render={({ field, fieldState }) => {
                        const { error } = fieldState
                        const isError = error || field.value == 'Invalid Date'
                        return (
                          <DatePicker
                            label="Select Start Date"
                            value={field.value}
                            onChange={(date) => {
                              field.onChange(date)
                              setValue('endDate', getEndDateOfMonth(date))
                            }}
                            format="d MMM yyyy"
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                variant: 'outlined',
                                helperText: isError
                                  ? error?.message || 'Invalid Date'
                                  : null,
                                error: !!isError,
                              },
                            }}
                          />
                        )
                      }}
                    />
                  </Grid>
                  <Grid item={true} xs={12}>
                    <Controller
                      control={control}
                      name={'endDate'}
                      rules={{ required: 'This is required.' }}
                      render={({ field, fieldState }) => {
                        const { error } = fieldState
                        const isError = error || field.value == 'Invalid Date'
                        return (
                          <DatePicker
                            label="Select End Date"
                            value={field.value}
                            onChange={(date) => field.onChange(date)}
                            format="d MMM yyyy"
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                variant: 'outlined',
                                helperText: isError
                                  ? error?.message || 'Invalid Date'
                                  : null,
                                error: !!isError,
                              },
                            }}
                          />
                        )
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item={true} xs={12}>
                <Controller
                  name={'discount'}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      size='small'
                      helperText={error ? error.message : null}
                      onChange={onChange}
                      value={value}
                      fullWidth
                      label={'Discount'}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </AppDialog>
  )
}
